import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEO from '../components/SEO';

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchPostData = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/blog/posts/${slug}`
        );

        if (response.ok) {
          const data = await response.json();
          setPost(data);
        } else if (response.status === 404) {
          setError('Blog post not found');
        } else {
          setError('Failed to fetch blog post');
        }
      } catch (error) {
        console.error('Error fetching post:', error);
        setError('Network error. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchPostData();
  }, [slug]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="container py-5">
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <p>Loading blog post...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-5">
        <div className="text-center">
          <i className="bi bi-exclamation-triangle fs-1 text-danger mb-3"></i>
          <h2 className="text-danger mb-3">Error</h2>
          <p className="text-muted mb-4">{error}</p>
          <Link to="/blog" className="btn btn-primary">
            <i className="bi bi-arrow-left me-2"></i>
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="container py-5">
        <div className="text-center">
          <i className="bi bi-journal-x fs-1 text-muted mb-3"></i>
          <h2 className="text-muted mb-3">Post Not Found</h2>
          <p className="text-muted mb-4">The blog post you're looking for doesn't exist.</p>
          <Link to="/blog" className="btn btn-primary">
            <i className="bi bi-arrow-left me-2"></i>
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO 
        title={post.meta_title || post.title}
        description={post.meta_description || post.excerpt}
        keywords={post.tags ? post.tags.join(', ') : 'WeFlare, blog, youth empowerment'}
        image={post.featured_image}
      />
      
      {/* JSON-LD Structured Data for SEO */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          "headline": post.title,
          "description": post.excerpt || post.meta_description,
          "image": post.featured_image,
          "author": {
            "@type": "Person",
            "name": post.author_name || "WeFlare Team"
          },
          "publisher": {
            "@type": "Organization",
            "name": "WeFlare",
            "logo": {
              "@type": "ImageObject",
              "url": `${window.location.origin}/logo.png`
            }
          },
          "datePublished": post.published_at,
          "dateModified": post.updated_at || post.published_at,
          "keywords": post.tags ? post.tags.join(', ') : '',
          "articleSection": "Youth Empowerment",
          "wordCount": post.content ? post.content.replace(/<[^>]*>/g, '').split(' ').length : 0
        })}
      </script>
      
      <article className="container py-5">
        {/* Breadcrumb */}
        <nav aria-label="breadcrumb" className="mb-4">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/" className="text-decoration-none">Home</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/blog" className="text-decoration-none">Blog</Link>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              {post.title}
            </li>
          </ol>
        </nav>

        <div className="row justify-content-center">
          <div className="col-lg-8">
            {/* Post Header */}
            <header className="mb-5">
              {/* Tags */}
              {post.tags && post.tags.length > 0 && (
                <div className="mb-3">
                  {post.tags.map((tag, index) => (
                    <span key={index} className="badge bg-primary-light text-primary me-2">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
              
              {/* Title */}
              <h1 className="display-4 mb-4" style={{ lineHeight: '1.2' }}>
                {post.title}
              </h1>
              
              {/* Meta Info */}
              <div className="d-flex flex-wrap align-items-center text-muted mb-4">
                <div className="me-4 mb-2">
                  <i className="bi bi-person me-2"></i>
                  <strong>{post.author_name || 'WeFlare Team'}</strong>
                </div>
                <div className="me-4 mb-2">
                  <i className="bi bi-calendar me-2"></i>
                  {formatDate(post.published_at)}
                </div>
                {post.view_count > 0 && (
                  <div className="me-4 mb-2">
                    <i className="bi bi-eye me-2"></i>
                    {post.view_count} views
                  </div>
                )}
              </div>
              
              {/* Featured Image */}
              {post.featured_image && (
                <div className="mb-5">
                  <img
                    src={post.featured_image}
                    alt={post.title}
                    className="img-fluid rounded shadow"
                    style={{ width: '100%', height: 'auto' }}
                  />
                </div>
              )}
              
              {/* Excerpt */}
              {post.excerpt && (
                <div className="lead text-muted mb-5 p-4 bg-light rounded">
                  {post.excerpt}
                </div>
              )}
            </header>

            {/* Post Content */}
            <div 
              className="blog-content mb-5"
              dangerouslySetInnerHTML={{ __html: post.content }}
              style={{
                lineHeight: '1.8',
                fontSize: '1.1rem'
              }}
            />

            {/* Share Section */}
            <div className="border-top pt-4 mb-5">
              <h5 className="mb-3">Share this post</h5>
              <div className="d-flex gap-2">
                <a
                  href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(window.location.href)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline-primary btn-sm"
                >
                  <i className="bi bi-twitter me-1"></i>
                  Twitter
                </a>
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline-primary btn-sm"
                >
                  <i className="bi bi-facebook me-1"></i>
                  Facebook
                </a>
                <a
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline-primary btn-sm"
                >
                  <i className="bi bi-linkedin me-1"></i>
                  LinkedIn
                </a>
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => {
                    navigator.clipboard.writeText(window.location.href);
                    alert('Link copied to clipboard!');
                  }}
                >
                  <i className="bi bi-link me-1"></i>
                  Copy Link
                </button>
              </div>
            </div>

            {/* Navigation */}
            <div className="d-flex justify-content-between align-items-center border-top pt-4">
              <Link to="/blog" className="btn btn-outline-primary">
                <i className="bi bi-arrow-left me-2"></i>
                Back to Blog
              </Link>
              
              <Link to="/donate" className="btn btn-primary">
                <i className="bi bi-heart me-2"></i>
                Support Our Mission
              </Link>
            </div>
          </div>
        </div>
      </article>

      {/* Custom CSS for blog content - moved to index.css */}
      <style>
        {`
        .blog-content h2 {
          margin-top: 2rem;
          margin-bottom: 1rem;
          color: #2c3e50;
        }
        
        .blog-content h3 {
          margin-top: 1.5rem;
          margin-bottom: 0.75rem;
          color: #34495e;
        }
        
        .blog-content p {
          margin-bottom: 1.25rem;
        }
        
        .blog-content img {
          max-width: 100%;
          height: auto;
          border-radius: 0.5rem;
          margin: 1.5rem 0;
        }
        
        .blog-content blockquote {
          border-left: 4px solid #007bff;
          padding-left: 1rem;
          margin: 1.5rem 0;
          font-style: italic;
          background-color: #f8f9fa;
          padding: 1rem;
          border-radius: 0.25rem;
        }
        
        .blog-content ul, .blog-content ol {
          margin-bottom: 1.25rem;
          padding-left: 2rem;
        }
        
        .blog-content li {
          margin-bottom: 0.5rem;
        }
        `}
      </style>
    </>
  );
};

export default BlogPost;